// Pure helpers that turn schedule-page state into the rendered job list. No Angular here.

export const SCHED_NORMAL = 1;
export const SCHED_OFFICE = 2;

export interface ScheduleViewState {
  scheduleDate: string;        // MM-DD-YYYY the user picked
  dayJobs: any[];              // whole day's list (feeds search / filters)
  dayJobsDate: string;         // date dayJobs belongs to
  pagedJobs: any[];            // pages loaded so far for the paged view
  pagedJobsDate: string;       // date pagedJobs belongs to
  employeeFilter: string;
  selectedRegion: string[];
  regionFilterActive: boolean;
  jobtypeFilterType: string;
  filterjobtypeactive: boolean;
  filterBySiteVisit: boolean;
  scheduledOnly: boolean;      // "Scheduled only" switch: keep jobs whose status is still Scheduled
  loadFullList: boolean;
  initialAllLoaded: boolean;
  version: number;
  role: string;
  employeeID: any;
}

export interface ScheduleView {
  jobs: any[];
  hoursJobs: any[];
  filterActivated: boolean;
  usingFullList: boolean;
  pending: boolean;            // source list for scheduleDate has not arrived yet
}

function jobtype(job: any): string {
  return (job?.customer_jobtype || '').toString().toLowerCase();
}

export function isQIJob(job: any): boolean {
  const t = jobtype(job);
  return t.includes('qi') || t.includes('draincam') || t.includes('framing') || t.includes('swppp');
}

export function isEnergyJob(job: any): boolean {
  const t = jobtype(job);
  return !isQIJob(job) && !t.includes('specialist') && !t.includes('mf') && job.sitevisitrqd === true;
}

export function filterByJobType(type: string, jobs: any[]): any[] {
  switch ((type || '').toLowerCase()) {
    case 'qi': return jobs.filter(j => isQIJob(j));
    case 'mf': return jobs.filter(j => jobtype(j).includes('mf'));
    case 'eg': return jobs.filter(j => isEnergyJob(j));
    default: return jobs;
  }
}

function regionOf(job: any): string {
  return job.region ? job.region.toString().trim().toUpperCase() : '';
}

function customerOf(job: any): string {
  return (job.customer_name || '').toString().toLowerCase().trim();
}

// Region multi-select, with the DR Horton LOU / NE TN / NSH carve-outs from the KNX list.
export function filterByRegion(selected: string[], jobs: any[]): any[] {
  if (!selected || selected.length === 0) { return jobs; }
  const lou = selected.includes('LOU');
  const netn = selected.includes('NETN');
  const knx = selected.includes('KNX');
  const nsh = selected.includes('NSH');

  let out = jobs.filter(j => selected.includes(regionOf(j)));
  const add = (j: any) => { if (!out.includes(j)) { out.push(j); } };

  if (lou) {
    jobs.filter(j => customerOf(j).includes('dr horton lou') && regionOf(j) !== 'LOU').forEach(add);
  }
  if (netn) {
    jobs.filter(j => customerOf(j).includes('dr horton ne tn') && regionOf(j) !== 'NETN').forEach(add);
  }
  if (knx) {
    if (!lou) { out = out.filter(j => !customerOf(j).includes('dr horton lou')); }
    if (!netn) { out = out.filter(j => !customerOf(j).includes('dr horton ne tn')); }
    if (!nsh) { out = out.filter(j => !customerOf(j).includes('dr horton nsh')); }
  }
  return out;
}

function matchesVersion(job: any, version: number): boolean {
  return version === SCHED_OFFICE
    ? job.sitevisitrqd === false
    : (job.sitevisitrqd === true || job.istrip === true);
}

function isProjected(job: any): boolean {
  return ((job?.job_status || '') as string).trim() === 'Projected';
}

// Status is exactly "Scheduled" (any case/padding); blank, Reschedule, Fail*, etc. are not.
export function isScheduledStatus(status: any): boolean {
  return (status || '').toString().trim().toLowerCase() === 'scheduled';
}

export function deriveScheduleView(s: ScheduleViewState): ScheduleView {
  const name = (s.employeeFilter || '').trim().toLowerCase();
  const anyFilter = !!name || s.regionFilterActive || s.filterjobtypeactive || s.filterBySiteVisit || s.scheduledOnly;
  const usingFullList = anyFilter || s.loadFullList;

  const source = usingFullList ? s.dayJobs : s.pagedJobs;
  const sourceDate = usingFullList ? s.dayJobsDate : s.pagedJobsDate;
  const pending = !s.scheduleDate || sourceDate !== s.scheduleDate;

  let jobs = pending ? [] : (source || []).filter(j => matchesVersion(j, s.version) && !isProjected(j));

  if (name) {
    jobs = jobs.filter(j => (j.employee_name || '').toString().toLowerCase().includes(name));
  }
  if (s.filterjobtypeactive) { jobs = filterByJobType(s.jobtypeFilterType, jobs); }
  if (s.regionFilterActive) { jobs = filterByRegion(s.selectedRegion, jobs); }
  if (s.filterBySiteVisit) { jobs = jobs.filter(j => j.sitevisitrqd === true); }
  if (s.scheduledOnly) { jobs = jobs.filter(j => isScheduledStatus(j.job_status)); }

  // Hours header: filtered set when filtering, else the whole day once it is here.
  let hoursJobs: any[];
  if (anyFilter) {
    hoursJobs = jobs;
  } else if (s.initialAllLoaded) {
    hoursJobs = s.dayJobsDate === s.scheduleDate ? (s.dayJobs || []) : [];
  } else {
    hoursJobs = jobs;
  }
  if (s.role === 'User') {
    hoursJobs = hoursJobs.filter(j => j.employee_id == s.employeeID);
  }

  return { jobs, hoursJobs, filterActivated: anyFilter, usingFullList, pending };
}
